import type { PersonEfteruddannelse } from "@/interfaces/schema";
import { roundLessons } from "@/util/number";

// Fælles lektionstype-oversigt for påbud og pålæg. De indberettede linjer
// ligger på påbuddets/pålæggets egen indberetning; de overførte linjer ligger
// på den efteruddannelsesperiode-indberetning, som overskuddet blev flyttet
// til (OverfoertTilPersonEfteruddannelseIndberetningId).
export interface InjunctionLessonTypeLinje {
  efteruddannelsestypenr: number;
  indberettedeLektioner: number;
}

export interface InjunctionLessonTypeRow {
  typeNr: number;
  typeLabel: string;
  reported: number;
  transferred: number;
}

const getTypeLabel = (typeNr: number, navn?: string | null): string =>
  navn?.trim() || String(typeNr);

export const buildInjunctionLessonTypeRows = (
  reportedLinjer: InjunctionLessonTypeLinje[] | undefined,
  transferredLinjer: InjunctionLessonTypeLinje[] | undefined,
  allTypes?: KodeListe[],
): InjunctionLessonTypeRow[] => {
  const rowsByType = new Map<number, InjunctionLessonTypeRow>();

  const getRow = (
    typeNr: number,
    navn?: string | null,
  ): InjunctionLessonTypeRow => {
    const existing = rowsByType.get(typeNr);
    if (existing) return existing;

    const row: InjunctionLessonTypeRow = {
      typeNr,
      typeLabel: getTypeLabel(typeNr, navn),
      reported: 0,
      transferred: 0,
    };
    rowsByType.set(typeNr, row);

    return row;
  };

  allTypes?.forEach((type) => getRow(type.nr, type.navn));

  reportedLinjer?.forEach((linje) => {
    const row = getRow(linje.efteruddannelsestypenr);
    row.reported = roundLessons(row.reported + linje.indberettedeLektioner);
  });

  transferredLinjer?.forEach((linje) => {
    const row = getRow(linje.efteruddannelsestypenr);
    row.transferred = roundLessons(
      row.transferred + linje.indberettedeLektioner,
    );
  });

  return [...rowsByType.values()].sort((a, b) => a.typeNr - b.typeNr);
};

export const sumInjunctionLessonTypeColumn = (
  rows: InjunctionLessonTypeRow[],
  column: "reported" | "transferred",
): number => roundLessons(rows.reduce((total, row) => total + row[column], 0));

export const findTransferredLinjer = (
  periods: PersonEfteruddannelse[],
  targetIndberetningId: string | null | undefined,
): InjunctionLessonTypeLinje[] => {
  if (!targetIndberetningId) return [];

  for (const period of periods) {
    const match = period.personEfteruddannelseIndberetning.find(
      (indberetning) => indberetning.id === targetIndberetningId,
    );
    if (match) return match.personEfteruddannelseIndberetningLinje;
  }

  return [];
};
